/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Switch } from 'antd'
import React, { useMemo, useState } from 'react'
import { getGameTypesBySportId } from '@sportninja/common/selectors/types'
// react-redux here is v5, which has no hooks -- `src/hooks/useRedux` is the
// codebase's store-subscription shim for exactly this.
import { useSelector } from 'src/hooks/useRedux'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  useScheduleTeams,
  useSubCompetitions,
  useVenueFacilities,
  useVenues,
} from '@sportninja/common/features/game/gameForm'
import type {
  GameEntityLike,
  GameFormMode,
} from '@sportninja/common/features/game/gameForm'
import LocationModal from 'src/components/LocationModal'
import type { CreatedVenue } from 'src/components/LocationModal/LocationContext'
import GameModal from './GameModal'
import type { SearchFieldOption } from './GameSearchField'

interface Props {
  isOpen: boolean
  onClose: () => void
  mode: GameFormMode
  /** Edit only — the parsed game entity. */
  game?: GameEntityLike | null
  /** The competition the game belongs to; scopes the team and venue lists. */
  scheduleId?: string | null
  /** Required to create a location from inside the modal. */
  organizationId?: string | null
  /** Org name, shown on the location scope toggle ("Within <org>"). */
  organizationName?: string | null
  /** Sport whose game types are offered. */
  sportId?: string | null
  onSaved?: (response: unknown) => void
  onDeleted?: () => void
  /** Overrides the delete gate when the game object cannot answer it. */
  canDelete?: boolean
}

/**
 * Wires the game modal to its data.
 *
 * Kept separate from `GameModal` so the modal itself stays presentational and
 * screen-agnostic: this is the only piece that knows about redux, React Query
 * and the nested location modal, and it is what call sites mount.
 */
const GameModalContainer: React.FC<Props> = ({
  isOpen,
  onClose,
  mode,
  game,
  scheduleId,
  organizationId,
  organizationName,
  sportId,
  onSaved,
  onDeleted,
  canDelete,
}) => {
  const [creatingLocation, setCreatingLocation] = useState(false)
  /*
   * The legacy picker's "All Locations" / "Within <org>" switch. ON lists every
   * location; OFF re-queries with `recursive=0`, narrowing to the competition's
   * own. Defaults ON, as legacy did.
   */
  const [showAllLocations, setShowAllLocations] = useState(true)
  const [pendingVenueId, setPendingVenueId] = useState<string | null>(null)

  // Options are fetched only while the modal is open, so opening the Game page
  // does not pay for three lists the user may never see.
  const { teams, isLoading: teamsLoading } = useScheduleTeams(
    isOpen ? scheduleId : null
  )
  const {
    venues,
    isLoading: venuesLoading,
    refetch: refetchVenues,
  } = useVenues(scheduleId, showAllLocations, isOpen)

  /*
   * Divisions, fetched only once a PLACEHOLDER team is selected. Every other
   * game already knows its division, so this stays idle in the common case
   * rather than pulling a list the admin will never see.
   */
  const [needsSubCompetition, setNeedsSubCompetition] = useState(false)
  const {
    subCompetitions,
    subCompetitionLabel,
    isLoading: subsLoading,
    subCompetitionsResolved,
  } = useSubCompetitions(scheduleId, isOpen && needsSubCompetition)

  const [selectedVenueId, setSelectedVenueId] = useState<string | null>(null)
  const { facilities, isLoading: facilitiesLoading } =
    useVenueFacilities(selectedVenueId)

  /*
   * Read the sports slice, then derive the game types for this sport locally.
   *
   * Selecting the game types directly through the shim does NOT work here: it
   * only re-reads on store dispatches, and the sport types are already loaded
   * by `App`'s `requestTypesAllSports()` before this modal ever mounts. With no
   * further dispatch the captured value never refreshes, so a `sportId` that
   * arrives late (or a store that settled first) leaves the list permanently
   * empty. `getGameTypesBySportId` also returns a fresh [] on a miss, which the
   * shim's identity check would reject anyway.
   */
  const sportsState = useSelector<unknown>((state) => state?.types?.sports)

  const gameTypes = useMemo(
    () =>
      (getGameTypesBySportId({ types: { sports: sportsState } }, sportId) ||
        []) as Array<{ id: string; name?: string; name_full?: string }>,
    [sportsState, sportId]
  )

  const teamOptions: SearchFieldOption[] = useMemo(
    () =>
      teams.map((team) => ({
        value: team.id,
        label: team.name_full || team.name || '',
      })),
    [teams]
  )

  const venueOptions: SearchFieldOption[] = useMemo(
    () =>
      venues.map((venue) => ({
        value: venue.id,
        label: venue.name_full || venue.name || '',
      })),
    [venues]
  )

  const facilityOptions: SearchFieldOption[] = useMemo(
    () =>
      facilities.map((facility) => ({
        value: facility.id,
        label: facility.name || '',
      })),
    [facilities]
  )

  // `name` is the display label ("Regular Season"); `name_full` carries the raw
  // enum ("REGULAR_SEASON"), so read `name` exactly as the legacy select did.
  const subCompetitionOptions: SearchFieldOption[] = useMemo(
    () =>
      subCompetitions.map((sub) => ({
        value: sub.id,
        label: sub.name_full || sub.name || '',
      })),
    [subCompetitions]
  )

  const gameTypeOptions: SearchFieldOption[] = useMemo(
    () =>
      (gameTypes || []).map((type) => ({
        value: String(type.id),
        label: type.name || '',
      })),
    [gameTypes]
  )

  /**
   * The venue just created is selected directly from the modal's callback --
   * no re-list-and-guess. `pendingVenueId` carries it until the refetched list
   * contains it, so the picker can show it without waiting on the request.
   */
  const handleLocationCreated = async (venue?: CreatedVenue) => {
    setCreatingLocation(false)
    /*
     * Refetch BEFORE selecting, as legacy did -- it fetched the venue list and
     * only selected inside the `.then()`. Selecting a venue looks its timezone
     * up in the loaded list, so announcing the new id first meant that lookup
     * could run against the pre-create list, miss, and leave the game with no
     * timezone to re-zone into.
     */
    await refetchVenues()
    if (venue?.id) {
      setPendingVenueId(venue.id)
    }
  }

  const locationLabel = capitalizeWords(t('common:location', {}))

  /*
   * Only offered when the list is competition-scoped: with no schedule id the
   * query is already unscoped, so the switch would have nothing to narrow.
   * Matches the legacy `typeof parentId === 'string'` gate.
   */
  const locationScopeToggle = scheduleId ? (
    <div
      css={scopeRowStyle}
      onMouseDown={(event) => {
        // The select closes on blur; mousedown fires first so the toggle is not
        // lost to the dropdown unmounting under the pointer.
        event.preventDefault()
        setShowAllLocations((v) => !v)
      }}
    >
      <span css={scopeLabelStyle}>
        {showAllLocations
          ? 'All Locations'
          : `Within ${organizationName || 'Organization'}`}
      </span>
      <Switch size='small' checked={showAllLocations} />
    </div>
  ) : null

  /*
   * Offered only with an organization in scope.
   *
   * Legacy fell back to a schedule-scoped `POST /schedules/{id}/venues` when
   * there was no org id. `LocationModal` posts exclusively to
   * `/organizations/{id}/venues`, so it cannot serve that path -- showing the
   * row without an org would open a modal whose Save is guaranteed to fail.
   * Hiding it is the honest degradation until the modal learns the
   * schedule-scoped endpoint.
   *
   * In practice every caller supplies one (Schedule page, Game page and the
   * games grid all thread it through), so this only bites a competition with
   * no organization at all.
   */
  const createLocationRow = organizationId ? (
    <button
      type='button'
      css={createRowStyle}
      onMouseDown={(event) => {
        // The select closes on blur; mousedown fires first so the click is not
        // lost to the dropdown unmounting under the pointer.
        event.preventDefault()
        setCreatingLocation(true)
      }}
    >
      + Create a new {locationLabel.toLowerCase()}
    </button>
  ) : null

  return (
    <>
      <GameModal
        isOpen={isOpen}
        onClose={onClose}
        mode={mode}
        game={game}
        scheduleId={scheduleId}
        onSaved={onSaved}
        onDeleted={onDeleted}
        canDelete={canDelete}
        teamOptions={teamOptions}
        teams={teams}
        venues={venues}
        venueOptions={venueOptions}
        facilityOptions={facilityOptions}
        gameTypeOptions={gameTypeOptions}
        subCompetitionOptions={subCompetitionOptions}
        subCompetitionLabel={subCompetitionLabel || undefined}
        subCompetitionsLoading={subsLoading}
        subCompetitionsResolved={subCompetitionsResolved}
        onPlaceholderTeamChange={setNeedsSubCompetition}
        teamsLoading={teamsLoading}
        venuesLoading={venuesLoading}
        facilitiesLoading={facilitiesLoading}
        locationScopeToggle={locationScopeToggle}
        createLocationRow={createLocationRow}
        onVenueChange={setSelectedVenueId}
        preselectVenueId={pendingVenueId}
      />

      {/*
        Deliberately no `scheduleId`/`flightId`: those make LocationModal attach
        the new venue to that schedule's SCHEDULER selected-venues pool, which
        is a SchedulerWizard concept and not what creating a location from the
        game form means. The attach is wrapped in a silent catch upstream, so a
        wrong one would fail invisibly.
      */}
      {organizationId && (
        <LocationModal
          isOpen={creatingLocation}
          onClose={() => setCreatingLocation(false)}
          organizationId={organizationId}
          onSuccess={handleLocationCreated}
        />
      )}
    </>
  )
}

export default GameModalContainer

// ─── Styles ────────────────────────────────────────────────────────────────

const scopeRowStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: ${colors.SECONDARY_200};
  }
`

const scopeLabelStyle = css`
  font-size: 13px;
  color: ${colors.WHITE};
`

const createRowStyle = css`
  display: block;
  width: 100%;
  padding: 6px 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${colors.PRIMARY};
  font-size: 14px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${colors.SECONDARY_200};
  }
`
